import React, {useEffect, useState, useRef, useMemo, useCallback} from "react";
import {Form} from "react-router-dom";
import {toast} from "react-toastify";
import {Autocomplete, useLoadScript} from "@react-google-maps/api";

import Loading from "../common/Loading";
import EditOrgMutation from "./mutations/EditOrg";
import DualListSelector from "../common/DualListSelector";
import {CURRENCIES} from "../../constants/vars";
import {GlobalStyles} from "../../constants/styles";
import {gql, useMutation, useQuery} from "@apollo/client";
import FilePicker from "../common/FilePicker";
import {UPDATE_ORGANIZATION_LOGO} from "./mutations/UpdateOrganizationLogo";
import useHasFeature from "../common/hooks/useHasFeature";
import useThemeColors from "../organization/portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";

const STREAMING_PROVIDERS = gql`query YouTubeProviders { youtubeProviders { id name isAngeonxDefault } }`;

export default function EditOrgModal({
                                         show,
                                         organization,
                                         setOrganizations,
                                         onClose,
                                         fields,
                                         waivers,
                                         sanctionRules,
                                         stripeAccounts,
                                     }) {
    const [editOrgMutation] = EditOrgMutation();
    const [updateOrganizationLogo] = useMutation(UPDATE_ORGANIZATION_LOGO);
    const {t} = useTranslation();

    const scopeId = useMemo(
        () => `kbola-edit-org-${Math.random().toString(36).slice(2, 9)}`,
        []
    );

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const API_BASE = useMemo(() => process.env.REACT_APP_API_HOST, []);

    const {hasFeature} = useHasFeature(organization?.activeSubscription);
    const canUseWaiver = hasFeature("waiver_management");
    const canUseRules = hasFeature("sanction_management");
    const canUseStreaming = hasFeature("elite_stream_games");
    const {data: streamingProviderData} = useQuery(STREAMING_PROVIDERS, {
        skip: !canUseStreaming,
        fetchPolicy: "network-only",
    });

    const themeColors = useThemeColors();

    const normalizeUrl = useCallback(
        (u) => {
            if (!u) return null;
            if (/^(blob:|data:)/i.test(u)) return u;
            if (/^https?:\/\//i.test(u)) return u;

            const base = String(API_BASE || "").replace(/\/$/, "");
            if (!base) return u;

            return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
        },
        [API_BASE]
    );

    const {isLoaded} = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: ["places"],
    });

    const [name, setName] = useState("");
    const [orgType, setOrgType] = useState("league");
    const [waiverIds, setWaiverIds] = useState([]);
    const selectedWaiverTitles = useMemo(() => {
        const selectedSet = new Set((waiverIds || []).map((id) => String(id)));
        return (waivers || [])
            .filter((waiver) => selectedSet.has(String(waiver.id)))
            .map((waiver) => waiver.title);
    }, [waiverIds, waivers]);
    const [stripeAccountId, setStripeAccountId] = useState("");
    const [youtubeProviderId, setYoutubeProviderId] = useState("");
    const [sanctionRuleId, setSanctionRuleId] = useState("");
    const [currency, setCurrency] = useState("");
    const [selectedFields, setSelectedFields] = useState([]);
    const [location, setLocation] = useState({
        address: "",
        city: "",
        state: "",
        country: "",
        countryCode: "",
        latitude: null,
        longitude: null,
        placeId: "",
    });
    const [uploading, setUploading] = useState(false);

    const autocompleteRef = useRef(null);

    const flattenedFields = useMemo(() => {
        return (fields || []).flatMap((venue) =>
            venue?.fields?.map((field) => ({
                ...field,
                venueName: venue.name,
            })) || []
        );
    }, [fields]);

    const modalStyles = useMemo(() => {
        const primary = themeColors.primary;

        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(primary, 0.50)},
                    ${alphaFromHex(primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {display: "flex", alignItems: "center", gap: 10},
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: themeColors.textOnPrimary,
                background: themeColors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: "#14151a",
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {
                padding: 14,
                background: "#fff",
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: GlobalStyles.colors.woodsmoke,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 700,
                outline: "none",
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [themeColors]);

    useEffect(() => {
        if (organization) {
            setName(organization.name || "");
            setOrgType(organization.organizationType?.code || "league");
            setWaiverIds(
                organization?.waivers?.map((w) => w.id) ||
                (organization?.waiver?.id ? [organization.waiver.id] : [])
            );
            setStripeAccountId(organization.stripeAccount?.id || "");
            setYoutubeProviderId(organization.youtubeProvider?.id || "");
            setSanctionRuleId(organization.sanctionRule?.id || "");
            setCurrency(organization.currency || "");
            setSelectedFields(organization.fields || []);
            setLocation({
                address: organization.address || "",
                city: organization.city || "",
                state: organization.state || "",
                country: organization.country || "",
                countryCode: organization.countryCode || "",
                latitude: organization.latitude || null,
                longitude: organization.longitude || null,
                placeId: organization.placeId || "",
            });

            setLogoPreviewUrl(normalizeUrl(organization.logoUrl || null));
            setLogoFile(null);
        }
    }, [organization, normalizeUrl]);

    useEffect(() => {
        if (!logoFile) return;
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const clearLogo = () => {
        setLogoFile(null);
        setLogoPreviewUrl(normalizeUrl(organization?.logoUrl || null));
    };

    if (!show || !organization) return null;
    if (!isLoaded) return <Loading/>;

    function onLoad(autocomplete) {
        autocompleteRef.current = autocomplete;
    }

    function onPlaceChanged() {
        if (!autocompleteRef.current) return;

        const place = autocompleteRef.current.getPlace();
        if (!place.geometry) {
            alert(
                t("editOrganization.noDetailsForInput", {
                    name: place.name || "",
                })
            );
            return;
        }

        const locationObj = {
            address: place.formatted_address,
            city: "",
            state: "",
            country: "",
            countryCode: "",
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            placeId: place.place_id,
        };

        place.address_components?.forEach((component) => {
            const types = component.types || [];
            if (types.includes("locality")) locationObj.city = component.long_name;
            if (types.includes("administrative_area_level_1")) locationObj.state = component.long_name;
            if (types.includes("country")) {
                locationObj.country = component.long_name;
                locationObj.countryCode = component.short_name || "";
            }
        });

        setLocation(locationObj);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setUploading(true);

        try {
            const input = {
                orgId: organization.id,
                name,
                organizationType: orgType,
                waiverIds,
                sanctionRuleId: sanctionRuleId || null,
                location,
                fieldIds: selectedFields.map((f) => f.id),
                stripeAccountId: stripeAccountId || null,
                ...(canUseStreaming ? {youtubeProviderId: youtubeProviderId ? Number(youtubeProviderId) : 0} : {}),
                currency,
            };

            const {data: res} = await editOrgMutation({variables: input});

            const updatedOrg = res?.editTeam?.organization;
            if (updatedOrg) {
                if (logoFile) {
                    try {
                        const up = await updateOrganizationLogo({
                            variables: {organizationId: updatedOrg.id, file: logoFile},
                        });

                        const payload = up?.data?.updateOrganizationLogo;
                        if (!payload?.success) {
                            toast.warn(
                                t("editOrganization.logoUploadFailedWithErrors", {
                                    errors: (payload?.errors || []).join(", "),
                                }),
                                {
                                    autoClose: 4500,
                                    toastId: "org_logo_failed",
                                    position: "top-center",
                                }
                            );
                        } else if (payload?.organization) {
                            updatedOrg.logoUrl = payload.organization.logoUrl;
                        }
                    } catch (error) {
                        toast.warn(
                            t("editOrganization.logoUploadFailedWithMessage", {
                                message: error.message,
                            }),
                            {
                                autoClose: 4500,
                                toastId: "org_logo_failed",
                                position: "top-center",
                            }
                        );
                    }
                }

                toast.success(t("editOrganization.updatedSuccess"), {
                    autoClose: 3000,
                    toastId: "org_edited",
                    position: "top-center",
                    closeOnClick: true,
                });

                setOrganizations((orgs) =>
                    orgs.map((org) => (org.id === updatedOrg.id ? updatedOrg : org))
                );

                onClose();
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                autoClose: 3000,
                toastId: "org_error",
                position: "top-center",
                closeOnClick: true,
            });
        } finally {
            setUploading(false);
        }
    }

    return (
        <>
            <div
                className="modal fade show d-block"
                id="edit-org-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form onSubmit={handleSubmit}>
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>
                                        {t("editOrganization.editBadge")}
                                    </span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("editOrganization.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={t("editOrganization.close")}
                                    style={modalStyles.closeBtn}
                                    title={t("editOrganization.close")}
                                    disabled={uploading}
                                >
                                    <span aria-hidden="true" style={{fontSize: 20, lineHeight: 1}}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganization.basics")}
                                    </div>

                                    <FilePicker
                                        scopeId={scopeId}
                                        colors={themeColors}
                                        label={t("editOrganization.logoLabel")}
                                        hint={t("editOrganization.logoHint")}
                                        accept="image/*"
                                        value={logoFile}
                                        previewUrl={logoPreviewUrl}
                                        onChangeFile={(file) => {
                                            setLogoFile(file);
                                        }}
                                        onClear={clearLogo}
                                    />

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganization.name")}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder={t("editOrganization.namePlaceholder")}
                                            style={modalStyles.input}
                                        />
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganization.organizationType")}
                                        </label>
                                        <select
                                            className="form-control"
                                            required
                                            value={orgType}
                                            onChange={(e) => setOrgType(e.target.value)}
                                            style={modalStyles.select}
                                        >
                                            <option value="league">
                                                {t("editOrganization.typeLeague")}
                                            </option>
                                            <option value="academy">
                                                {t("editOrganization.typeAcademy")}
                                            </option>
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("editOrganization.organizationTypeHint")}
                                        </div>
                                    </div>

                                    {canUseStreaming && <div className="form-group" style={{marginBottom: 0, marginTop: 12}}>
                                        <label style={modalStyles.label}>{t("editOrganization.streamingProvider")}</label>
                                        <select
                                            className="form-control"
                                            value={youtubeProviderId}
                                            onChange={(e) => setYoutubeProviderId(e.target.value)}
                                            style={modalStyles.select}
                                        >
                                            <option value="">{t("editOrganization.useAngeonxDefault")}</option>
                                            {(streamingProviderData?.youtubeProviders || []).filter((provider) => !provider.isAngeonxDefault).map((provider) => (
                                                <option key={provider.id} value={provider.id}>{provider.name} ({t("editOrganization.youtubeAccount")})</option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>{t("editOrganization.streamingProviderHint")}</div>
                                    </div>}
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganization.location")}
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganization.address")}
                                        </label>
                                        <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t("editOrganization.addressPlaceholder")}
                                                value={location.address}
                                                onChange={(e) =>
                                                    setLocation((loc) => ({...loc, address: e.target.value}))
                                                }
                                                style={modalStyles.input}
                                            />
                                        </Autocomplete>
                                        <div style={modalStyles.hint}>
                                            {t("editOrganization.addressHint")}
                                        </div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganization.venues")}
                                    </div>
                                    <DualListSelector
                                        title={t("editOrganization.venues")}
                                        description=""
                                        leftTitle={t("editOrganization.availableFields")}
                                        rightTitle={t("editOrganization.selectedFields")}
                                        items={flattenedFields}
                                        selected={selectedFields}
                                        getItemLabel={(f) => `${f.name}`}
                                        onChange={(updated) => setSelectedFields(updated)}
                                    />
                                </div>

                                {(canUseWaiver || canUseRules) && (
                                    <div style={modalStyles.card}>
                                        <div style={modalStyles.sectionTitle}>
                                            {t("editOrganization.policiesAndRules")}
                                        </div>

                                        {canUseWaiver && (
                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("editOrganization.waiverOptional")}
                                                </label>
                                                <div className="kbola-waiver-picker-wrap">
                                                    <select
                                                        className="form-control kbola-waiver-picker"
                                                        multiple
                                                        value={waiverIds}
                                                        onChange={(e) =>
                                                            setWaiverIds(
                                                                Array.from(e.target.selectedOptions).map((opt) => opt.value)
                                                            )
                                                        }
                                                        style={modalStyles.select}
                                                        size={Math.min(6, Math.max(3, waivers?.length || 0))}
                                                    >
                                                        {waivers?.map((w) => (
                                                            <option key={w.id} value={w.id}>
                                                                {w.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {selectedWaiverTitles.length > 0 && (
                                                    <div className="kbola-waiver-chip-row">
                                                        {selectedWaiverTitles.map((title, index) => (
                                                            <span className="kbola-waiver-chip" key={`${title}-${index}`}>
                                                                {title}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {canUseRules && (
                                            <div className="form-group" style={{marginBottom: 0}}>
                                                <label style={modalStyles.label}>
                                                    {t("editOrganization.sanctionRuleOptional")}
                                                </label>
                                                <select
                                                    className="form-control"
                                                    value={sanctionRuleId}
                                                    onChange={(e) => setSanctionRuleId(e.target.value)}
                                                    style={modalStyles.select}
                                                >
                                                    <option value="">
                                                        {t("editOrganization.noRule")}
                                                    </option>
                                                    {sanctionRules?.map((rule) => (
                                                        <option key={rule.id} value={rule.id}>
                                                            {rule.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganization.billing")}
                                    </div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganization.currency")}
                                        </label>
                                        <select
                                            className="form-control"
                                            required
                                            value={currency}
                                            onChange={(e) => setCurrency(e.target.value)}
                                            style={modalStyles.select}
                                        >
                                            <option value="">
                                                {t("editOrganization.selectCurrency")}
                                            </option>
                                            {CURRENCIES?.map((c) => (
                                                <option key={c.value} value={c.value}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("editOrganization.currencyHint")}
                                        </div>
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganization.stripeAccount")}
                                        </label>
                                        <select
                                            className="form-control"
                                            value={stripeAccountId}
                                            onChange={(e) => setStripeAccountId(e.target.value)}
                                            style={modalStyles.select}
                                        >
                                            <option value="">
                                                {t("editOrganization.noAccount")}
                                            </option>
                                            {stripeAccounts?.map((sa) => (
                                                <option key={sa.id} value={sa.id}>
                                                    {sa.name}
                                                </option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("editOrganization.stripeAccountHint")}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={onClose}
                                    style={{...modalStyles.btn, ...modalStyles.btnGhost}}
                                    disabled={uploading}
                                >
                                    <i className="fas fa-times"/>
                                    {t("editOrganization.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{...modalStyles.btn, ...modalStyles.btnPrimary}}
                                    disabled={uploading}
                                    title={
                                        uploading
                                            ? t("editOrganization.saving")
                                            : t("editOrganization.saveChanges")
                                    }
                                >
                                    <i className="fas fa-check"/>
                                    {uploading
                                        ? t("editOrganization.saving")
                                        : t("editOrganization.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
        #edit-org-modal.modal {
          background: rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(2px);
        }

        #edit-org-modal .modal-dialog {
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          margin-top: 1.25rem;
          margin-bottom: 1.25rem;
        }

        #edit-org-modal .modal-content {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        #edit-org-modal .modal-body {
          overflow-y: auto;
          max-height: 66vh;
        }

        #edit-org-modal .form-control:focus {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #edit-org-modal .dual-list-selector,
        #edit-org-modal .dual-list {
          margin-top: 8px;
        }

        #edit-org-modal .kbola-label{
          font-size: 12.5px;
          font-weight: 800;
          color: rgba(20,21,26,0.70);
          margin-bottom: 6px;
          display: block;
        }

        #edit-org-modal .kbola-waiver-picker-wrap {
          border-radius: 12px;
          border: 1px solid rgba(0, 0, 0, 0.11);
          background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
          padding: 8px;
        }

        #edit-org-modal .kbola-waiver-picker {
          min-height: 132px;
          height: auto !important;
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }

        #edit-org-modal .kbola-waiver-picker option {
          border-radius: 9px;
          margin: 2px 0;
          padding: 9px 10px;
          font-weight: 700;
        }

        #edit-org-modal .kbola-waiver-picker option:checked {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
          color: #14151a;
        }

        #edit-org-modal .kbola-waiver-picker-wrap:focus-within {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
          box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
        }

        #edit-org-modal .kbola-waiver-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 8px;
        }

        #edit-org-modal .kbola-waiver-chip {
          font-size: 11px;
          font-weight: 800;
          border-radius: 999px;
          padding: 5px 9px;
          color: rgba(20, 21, 26, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.1);
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.42);
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        #edit-org-modal .kbola-waiver-picker::-webkit-scrollbar {
          width: 10px;
        }

        #edit-org-modal .kbola-waiver-picker::-webkit-scrollbar-thumb {
          border-radius: 999px;
          border: 2px solid rgba(255,255,255,0.95);
          background: rgba(20, 21, 26, 0.24);
        }

        @media (max-width: 768px) {
          #edit-org-modal .modal-dialog {
            max-width: 95%;
            margin: 0.9rem auto;
          }
          #edit-org-modal .modal-body {
            max-height: 70vh;
          }
        }
      `}</style>
        </>
    );
}
